import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActiveMethodicMaterialsService } from '@core/services/active-methodic-materials.service';
import { WordsService } from '@core/services/games';
import { AbstractSpeedreadingGameController } from '@games/core/controllers';
import { GameUserAnswer } from '@games/core/example/user-answer';
import { OrchestratorStage, provideDynamicOrchestrator } from '@games/shared/orchestrator';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BlindReadingService } from '../blind-reading.service';
import { BlindReadingTyping } from '../typing';
import { GameControllerDecorator } from '@games/core/controller';
import { BlindReadingOrchestrator } from './blind-reading-orchestrator';

const listAnimation = trigger('listAnimation', [
  transition('* <=> *', [
    query(
      ':enter',
      [style({ opacity: 0 }), stagger('60ms', animate('600ms ease-out', style({ opacity: 1 })))],
      {
        optional: true,
      },
    ),
    query(':leave', animate('200ms', style({ opacity: 0 })), { optional: true }),
  ]),
]);

const openClose = trigger('openClose', [
  transition(':enter', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
  transition(':leave', [animate('200ms', style({ opacity: 0 }))]),
]);

@Component({
  selector: 'msl-blind-reading-controller',
  animations: [openClose, listAnimation],
  templateUrl: './blind-reading-controller.component.html',
  styleUrls: ['./blind-reading-controller.component.scss'],
  providers: [provideDynamicOrchestrator(BlindReadingOrchestrator)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@GameControllerDecorator()
export class BlindReadingControllerComponent
  extends AbstractSpeedreadingGameController<BlindReadingTyping>
  implements OnInit
{
  public get orchestratorStage(): OrchestratorStage<BlindReadingOrchestrator> {
    // Add logic here
    return 'play';
  }

  spentTime = 0;

  t: Subscription = new Subscription();

  localFontSize = 20;

  words?: string[];

  blindHeight = 0;

  overlayHeight?: number;

  wordsWrapScrollHeight?: number;

  // Сколько нужно передвинуть вверх
  wordsWrapToTop = 0;

  constructor(
    protected _activeMethodicMaterialsService: ActiveMethodicMaterialsService,
    private _service: BlindReadingService,
    private _translate: TranslateService,
    private _wordsService: WordsService,
  ) {
    super();
  }

  @ViewChild('blind') blindEl?: ElementRef<HTMLElement>;

  @ViewChild('wordsWrap') wordsWrapEl?: ElementRef<HTMLElement>;

  @ViewChild('overlay') overlayEl?: ElementRef<HTMLElement>;

  blinding(overlay?: number, scrollHeight?: number) {
    const time =
      this.localFontSize * 1.5 * +this.parameters.wordsPerMinute * +this.parameters.wordsCount;

    if (scrollHeight && overlay && scrollHeight - overlay === 0) {
      this.t = this.time.times((1000 * 60) / time, overlay).subscribe({
        next: () => {
          this.blindHeight++;
          this.cdr.markForCheck();
        },
        complete: () => alert('complete'),
        error: (e) => console.error(e),
      });
    } else if (scrollHeight && overlay && scrollHeight - overlay !== 0) {
      const toTopTimes = scrollHeight + overlay;

      this.t = this.time.times((1000 * 60) / time, overlay * 0.25).subscribe({
        next: () => {
          this.blindHeight++;
          this.cdr.markForCheck();
        },
        complete: () => this.toTopUpper(toTopTimes, scrollHeight, overlay, time),
        error: (e) => console.error(e),
      });
    }
  }

  toTopUpper(toTopTimes: number, scrollHeight: number, overlay: number, time: number) {
    this.t = this.time.times((1000 * 60) / time, scrollHeight - overlay).subscribe({
      next: () => {
        this.wordsWrapToTop--;
        this.cdr.markForCheck();
      },
      complete: () => {
        this.t = this.time.times((1000 * 60) / time, overlay * 0.75).subscribe({
          next: () => {
            this.blindHeight++;
            this.cdr.markForCheck();
          },
          complete: () => {
            this.t.unsubscribe();
          },
        });
      },
      error: (e) => console.error(e),
    });
  }

  // TODO: define generateExample
  // generateExample() {
  //   this._wordsService.getWord().subscribe({
  //     next: (res) => {},
  //   });

  //   this.words = [
  //     'asdd',
  //     'qwer',
  //     'zxcv',
  //     'qazw',
  //     'sxed',
  //     'dcrf',
  //     'zxcz',
  //     'zxcv',
  //     'qwea',
  //     'poiu',
  //     'asdd',
  //     'qwer',
  //     'zxcv',
  //     'qazw',
  //     'sxed',
  //     'dcrf',
  //     'zxcz',
  //     'zxcv',
  //     'qwea',
  //     'poiu',
  //     'asdd',
  //     'qwer',
  //     'zxcv',
  //     'qazw',
  //     'sxed',
  //     'dcrf',
  //     'zxcz',
  //     'zxcv',
  //     'qwea',
  //     'poiu',
  //     'asdd',
  //     'qwer',
  //     'zxcv',
  //     'qazw',
  //     'sxed',
  //     'dcrf',
  //     'zxcz',
  //     'zxcv',
  //     'qwea',
  //     'poiu',
  //   ];
  //   setTimeout(() => {
  //     this.overlayHeight = this.overlayEl?.nativeElement.offsetHeight;
  //     this.wordsWrapScrollHeight = this.wordsWrapEl?.nativeElement.scrollHeight;

  //     this.blinding(this.overlayHeight, this.wordsWrapScrollHeight);
  //   }, 0);

  //   const example = null;
  //   const answer = null;
  //   return {
  //     example,
  //     answer,
  //   };
  // }

  // TODO: define example type
  msOnPlay() {
    // this.runCycle();
    this.words = [
      'asdd',
      'qwer',
      'zxcv',
      'qazw',
      'sxed',
      'dcrf',
      'zxcz',
      'zxcv',
      'qwea',
      'poiu',
      'asdd',
      'qwer',
      'zxcv',
      'qazw',
      'sxed',
      'dcrf',
      'zxcz',
      'zxcv',
      'qwea',
      'poiu',
      'asdd',
      'qwer',
      'zxcv',
      'qazw',
      'sxed',
      'dcrf',
      'zxcz',
      'zxcv',
      'qwea',
      'poiu',
      'asdd',
      'qwer',
      'zxcv',
      'qazw',
      'sxed',
      'dcrf',
      'zxcz',
      'zxcv',
      'qwea',
      'poiu',
    ];
    setTimeout(() => {
      this.overlayHeight = this.overlayEl?.nativeElement.offsetHeight;
      this.wordsWrapScrollHeight = this.wordsWrapEl?.nativeElement.scrollHeight;

      this.blinding(this.overlayHeight, this.wordsWrapScrollHeight);
    }, 0);
  }

  // onCycle(delta: number) {
  //   this.spentTime += delta;
  // }

  // TODO: define checkExample
  // checkAnswer() {
  //   return true;
  // }

  msOnFinish() {}

  clear() {
    this.blindHeight = 0;
    this.wordsWrapToTop = 0;
    this.t.unsubscribe();
  }

  msOnHalt() {
    this.clear();
  }

  _resetPlay() {
    this.clear();
  }

  _resetAnswer() {}

  _resetStop() {}

  ngOnInit() {}
}
